import type { PersistedItem, QuizCompletion, QuizRunPersistenceIdentity } from './types';

export const QUIZ_RUN_PERSISTENCE_VERSION = 1 as const;

/** Identity generation is deliberately not durable; it is an in-memory fence. */
export type QuizRunJournalIdentity = Omit<QuizRunPersistenceIdentity, 'identityGeneration'>;

export type QuizRunJournal = {
  version: typeof QUIZ_RUN_PERSISTENCE_VERSION;
  identity: QuizRunJournalIdentity;
  items: PersistedItem[];
  completionRequested: boolean;
  completion: QuizCompletion | null;
  completionReconciled: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

function isNullableSmallint(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (Number.isInteger(value) && (value as number) >= -32768 && (value as number) <= 32767)
  );
}

function isGuessSequence(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((guess) => Number.isInteger(guess) && guess >= 0 && guess <= 3) &&
    new Set(value).size === value.length
  );
}

function isIdentity(value: unknown): value is QuizRunJournalIdentity {
  if (!isRecord(value)) return false;
  return (
    isNonEmptyString(value.quizId) &&
    isNonEmptyString(value.editionId) &&
    isNonEmptyString(value.userId) &&
    typeof value.isAnonymous === 'boolean'
  );
}

function isPersistedItem(value: unknown): value is PersistedItem {
  if (!isRecord(value)) return false;
  if (
    !isNonEmptyString(value.editionItemId) ||
    (value.itemType !== 'trivia' && value.itemType !== 'poll') ||
    !isNonNegativeInteger(value.revision) ||
    !isNonNegativeInteger(value.acknowledgedRevision) ||
    value.acknowledgedRevision > value.revision
  ) {
    return false;
  }

  if (value.itemType === 'trivia') {
    return (
      isGuessSequence(value.guessSequence) &&
      isNonNegativeInteger(value.xpEarned) &&
      isNullableSmallint(value.rating) &&
      isNullableSmallint(value.feedback) &&
      (value.feedbackText === undefined ||
        value.feedbackText === null ||
        typeof value.feedbackText === 'string')
    );
  }

  return (
    isNonEmptyString(value.pollDefinitionId) &&
    isNonEmptyString(value.optionId) &&
    (value.sessionId === undefined ||
      value.sessionId === null ||
      isNonEmptyString(value.sessionId)) &&
    (value.xpEarned === undefined || isNonNegativeInteger(value.xpEarned))
  );
}

function isCompletion(value: unknown): value is QuizCompletion {
  if (!isRecord(value)) return false;
  return (
    isNonEmptyString(value.completionId) &&
    isNonEmptyString(value.quizId) &&
    isNonEmptyString(value.editionId) &&
    typeof value.emojiResults === 'string' &&
    isNonNegativeInteger(value.accuracy) &&
    value.accuracy <= 100 &&
    isNonNegativeInteger(value.totalQuestions) &&
    isNonNegativeInteger(value.xpEarned) &&
    isNonNegativeInteger(value.longestStreak) &&
    (value.createdAt === undefined || typeof value.createdAt === 'string')
  );
}

/** JSON encoding is deliberately synchronous so staging precedes network I/O. */
export function encodeQuizRunJournal(journal: QuizRunJournal): string {
  return JSON.stringify(journal);
}

/** Corrupt or incompatible journals are ignored rather than blocking a run. */
export function decodeQuizRunJournal(raw: string | null): QuizRunJournal | null {
  if (!raw) return null;

  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || value.version !== QUIZ_RUN_PERSISTENCE_VERSION) return null;
    if (!isIdentity(value.identity) || !Array.isArray(value.items)) return null;
    if (!value.items.every(isPersistedItem)) return null;
    const itemIds = value.items.map((item) => item.editionItemId);
    if (new Set(itemIds).size !== itemIds.length) return null;
    if (typeof value.completionRequested !== 'boolean') return null;
    if (value.completion !== null && !isCompletion(value.completion)) return null;
    if (typeof value.completionReconciled !== 'boolean') return null;
    if (value.completionReconciled && value.completion === null) return null;
    if (value.completion !== null && !value.completionRequested) return null;

    return {
      version: QUIZ_RUN_PERSISTENCE_VERSION,
      identity: value.identity,
      items: value.items,
      completionRequested: value.completionRequested,
      completion: value.completion,
      completionReconciled: value.completionReconciled,
    };
  } catch {
    return null;
  }
}

export function cloneJournal(journal: QuizRunJournal): QuizRunJournal {
  return (
    decodeQuizRunJournal(encodeQuizRunJournal(journal)) ?? {
      version: QUIZ_RUN_PERSISTENCE_VERSION,
      identity: { ...journal.identity },
      items: [],
      completionRequested: false,
      completion: null,
      completionReconciled: false,
    }
  );
}

export function createEmptyJournal(identity: QuizRunPersistenceIdentity): QuizRunJournal {
  return {
    version: QUIZ_RUN_PERSISTENCE_VERSION,
    identity: {
      quizId: identity.quizId,
      editionId: identity.editionId,
      userId: identity.userId,
      isAnonymous: identity.isAnonymous,
    },
    items: [],
    completionRequested: false,
    completion: null,
    completionReconciled: false,
  };
}
